/**
 * Tratamento de erros para o usuário.
 * Logs técnicos ficam no console; a UI só recebe texto em português, sem códigos internos.
 */

const GENERIC_ERROR =
  "Não foi possível concluir a operação. Tente novamente. Se o problema continuar, fale com o suporte.";

const ERROR_MESSAGES = {
  400: "Não foi possível salvar. Verifique as informações e tente novamente.",
  401: "Sessão expirada. Faça login novamente.",
  403: "Você não tem permissão para realizar esta ação.",
  404: "Registro não encontrado.",
  409: "Já existe um cadastro com esses dados.",
  422: "Há informações inválidas. Verifique os campos e tente novamente.",
  429: "Muitas tentativas. Aguarde um momento e tente novamente.",
  500: "Ocorreu um problema no servidor. Tente novamente mais tarde.",
  502: "Serviço temporariamente indisponível. Tente novamente mais tarde.",
  503: "Serviço temporariamente indisponível. Tente novamente mais tarde.",
  504: "A operação demorou demais. Tente novamente.",
};

const OPERATION_MESSAGES = {
  create: {
    success: "Item criado com sucesso!",
    error: "Não foi possível criar. Tente novamente.",
  },
  update: {
    success: "Item atualizado com sucesso!",
    error: "Não foi possível atualizar. Tente novamente.",
  },
  delete: {
    success: "Item excluído com sucesso!",
    error: "Não foi possível excluir. Tente novamente.",
  },
  fetch: {
    success: "Dados carregados com sucesso!",
    error: "Não foi possível carregar os dados. Tente novamente.",
  },
};

const CODE_MESSAGES = {
  PGRST204:
    "Não foi possível salvar agora. Tente novamente em alguns instantes. Se persistir, fale com o suporte.",
  PGRST116: "Registro não encontrado.",
  PGRST301: "Sessão expirada. Faça login novamente.",
  PGRST100: "A consulta não pôde ser processada. Tente novamente.",
  PGRST102: "Os dados enviados estão em formato inválido. Verifique e tente novamente.",
  "23505": "Já existe um cadastro com esses dados.",
  "23503": "Não foi possível concluir: existem informações vinculadas a este registro.",
  "23502": "Preencha todos os campos obrigatórios.",
  "22P02": "Há um dado em formato inválido. Verifique os campos e tente novamente.",
  "42501": "Você não tem permissão para realizar esta ação.",
  "42P01": "Serviço temporariamente indisponível. Tente novamente mais tarde.",
};

const TECHNICAL_PATTERNS = [
  /pgrst\d+/i,
  /schema cache/i,
  /could not find/i,
  /\bcolumn ['"`]/i,
  /\brelation ['"`]/i,
  /duplicate key value/i,
  /violates\s+(foreign|unique|not[- ]null|check)\s+constraint/i,
  /permission denied for/i,
  /row[- ]level security/i,
  /\bjwt\b/i,
  /http error!/i,
  /traceback/i,
  /psycopg|sqlalchemy|fastapi|postgrest/i,
  /internal server error/i,
  /null value in column/i,
  /invalid input syntax/i,
  /value too long for type/i,
  /failed to fetch/i,
  /networkerror/i,
  /econnrefused|enotfound|etimedout/i,
  /\b22p02\b|\b23505\b|\b23503\b|\b23502\b|\b42501\b|\b42p01\b/i,
  /^\s*\{[\s\S]*\}\s*$/,
  /['"]code['"]\s*:/,
  /\bselect\b[\s\S]*\bfrom\b/i,
  /undefined is not/i,
  /cannot read propert/i,
  /unexpected token/i,
  /syntaxerror/i,
  /typeerror/i,
  /referenceerror/i,
];

const USER_FACING_START =
  /^(não|nao|erro ao|já existe|ja existe|preencha|sessão|sessao|você|voce|verifique|aluno|monitor|usuário|usuario|dados |campo|tempo de|conect|arquivo|envie|senha|perfil|e-mail|email)/i;

function isReactNode(value) {
  return Boolean(value && typeof value === "object" && value.$$typeof);
}

function stripErrorPrefix(text) {
  return String(text || "")
    .replace(/^(error:\s*)+/i, "")
    .trim();
}

function extractStatusCode(error) {
  if (error == null) return null;
  if (typeof error === "number" && error >= 400) return error;
  if (error?.status) return error.status;
  if (error?.response?.status) return error.response.status;
  if (typeof error?.code === "number") return error.code;
  return null;
}

function extractErrorCode(error) {
  if (!error || typeof error !== "object") return "";
  const code =
    error.data?.code ||
    error.response?.data?.code ||
    (typeof error.code === "string" ? error.code : "");
  return String(code || "").trim();
}

function extractRawMessage(error) {
  if (error == null) return "";
  if (typeof error === "string") return stripErrorPrefix(error);
  if (typeof error === "number") return "";

  const data = error.data || error.response?.data;
  if (typeof data === "string") return stripErrorPrefix(data);
  if (data?.message && typeof data.message === "string") return stripErrorPrefix(data.message);
  if (typeof data?.detail === "string") return stripErrorPrefix(data.detail);
  if (error instanceof Error) return stripErrorPrefix(error.message);
  if (typeof error.message === "string") return stripErrorPrefix(error.message);
  if (typeof error.error === "string") return stripErrorPrefix(error.error);
  if (error.error) return extractRawMessage(error.error);
  return "";
}

function isTechnicalMessage(text) {
  if (!text) return true;
  if (text.length > 280) return true;
  return TECHNICAL_PATTERNS.some(pattern => pattern.test(text));
}

function looksLikeUserFacingMessage(text) {
  if (!text || isTechnicalMessage(text)) return false;
  if (/[<>\\]/.test(text) || /[{[]/.test(text)) return false;
  if (USER_FACING_START.test(text.trim())) return true;
  if (/[áàâãéêíóôõúçÁÀÂÃÉÊÍÓÔÕÚÇ]/.test(text)) return true;
  return false;
}

function messageForTechnicalText(text) {
  const raw = String(text || "");
  const lower = raw.toLowerCase();

  // Pydantic: "password: Value error, Senha deve ter no mínimo 8 caracteres"
  const valueErrorMatch = raw.match(/Value error,\s*(.+)$/i);
  if (valueErrorMatch && looksLikeUserFacingMessage(valueErrorMatch[1])) {
    return valueErrorMatch[1].trim();
  }
  if (/password/.test(lower) && (/at least (8|12)|mínimo (8|12)|minimo (8|12)|min_length/.test(lower))) {
    return "Senha deve ter no mínimo 8 caracteres";
  }
  if (/password/.test(lower) && (/maiúscula|maiuscula|caractere especial|uppercase|special/.test(lower))) {
    return "Senha deve conter: maiúscula, minúscula, número e caractere especial (!@#$%^&* etc)";
  }

  if (
    /schema cache/.test(lower) ||
    /could not find the ['`].+['`] column/.test(lower) ||
    /pgrst204/.test(lower)
  ) {
    return CODE_MESSAGES.PGRST204;
  }
  if (/duplicate key|unique constraint|já cadastrad|already exists/.test(lower)) {
    return CODE_MESSAGES["23505"];
  }
  if (/foreign key|still referenced|vinculad/.test(lower) && /violat|constraint/.test(lower)) {
    return CODE_MESSAGES["23503"];
  }
  if (/null value in column|not-null|not null/.test(lower)) {
    return CODE_MESSAGES["23502"];
  }
  if (/jwt|sessão expirada|session expired|not authenticated/.test(lower)) {
    return ERROR_MESSAGES[401];
  }
  if (/permission denied|row-level security|rls/.test(lower)) {
    return ERROR_MESSAGES[403];
  }
  if (
    /failed to fetch|networkerror|network request failed|fetch failed|econnrefused/.test(lower)
  ) {
    return "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.";
  }
  if (/timeout|timed out|esgotado/.test(lower)) {
    return "A operação demorou demais. Tente novamente.";
  }
  if (/\bcors\b/.test(lower)) {
    return "Não foi possível concluir por um problema de configuração. Fale com o suporte.";
  }
  if (/value is not a valid uuid|invalid uuid/.test(lower)) {
    return "Há um identificador inválido. Recarregue a página e tente novamente.";
  }
  if (/payload too large|file too large/.test(lower)) {
    return "O arquivo é grande demais. Envie um arquivo menor.";
  }
  if (/^bad request$/i.test(text)) return ERROR_MESSAGES[400];
  if (/^unauthorized$/i.test(text)) return ERROR_MESSAGES[401];
  if (/^forbidden$/i.test(text)) return ERROR_MESSAGES[403];
  if (/^not found$/i.test(text)) return ERROR_MESSAGES[404];
  if (/^conflict$/i.test(text)) return ERROR_MESSAGES[409];
  if (/^internal server error$/i.test(text)) return ERROR_MESSAGES[500];

  return null;
}

/**
 * Converte qualquer erro (API, PostgREST, Error, string) em mensagem para o usuário.
 */
export const toUserErrorMessage = error => {
  if (isReactNode(error)) return error;
  if (error == null || error === "") return GENERIC_ERROR;

  const status = extractStatusCode(error);
  const code = extractErrorCode(error).toUpperCase();
  if (CODE_MESSAGES[code]) return CODE_MESSAGES[code];

  const raw = extractRawMessage(error);
  const fromPattern = messageForTechnicalText(raw);
  if (fromPattern) return fromPattern;

  if (looksLikeUserFacingMessage(raw)) return raw;

  if (status && ERROR_MESSAGES[status]) return ERROR_MESSAGES[status];

  if (raw && !isTechnicalMessage(raw) && raw.length <= 180) {
    if (/\b(the|this|that|with|from|invalid|cannot|unable|failed|error|please|column|schema)\b/i.test(raw)) {
      return GENERIC_ERROR;
    }
    return raw;
  }

  return GENERIC_ERROR;
};

const extractErrorMessage = error => toUserErrorMessage(error);

export const formatError = (error, context = null) => {
  const message = toUserErrorMessage(error);
  if (looksLikeUserFacingMessage(typeof message === "string" ? message : "") && message !== GENERIC_ERROR) {
    return message;
  }
  if (context && OPERATION_MESSAGES[context]) {
    return OPERATION_MESSAGES[context].error;
  }
  return message;
};

export const formatSuccess = (operation, itemName = "Item") => {
  if (OPERATION_MESSAGES[operation]) {
    return OPERATION_MESSAGES[operation].success;
  }

  const messages = {
    create: `${itemName} criado com sucesso!`,
    update: `${itemName} atualizado com sucesso!`,
    delete: `${itemName} excluído com sucesso!`,
    fetch: "Dados carregados com sucesso!",
  };

  return messages[operation] || "Operação realizada com sucesso!";
};

export const isAuthError = error => {
  const statusCode = extractStatusCode(error);
  return statusCode === 401 || statusCode === 403;
};

export const isValidationError = error => {
  const statusCode = extractStatusCode(error);
  return statusCode === 400 || statusCode === 422;
};

export const isNetworkError = error => {
  const message = String(extractRawMessage(error) || toUserErrorMessage(error)).toLowerCase();
  return (
    message.includes("network") ||
    message.includes("conexão") ||
    message.includes("conectar") ||
    message.includes("fetch") ||
    message.includes("timeout")
  );
};

export const handleError = (error, context = null) => {
  const message = formatError(error, context);
  const statusCode = extractStatusCode(error);

  return {
    message,
    statusCode,
    isAuth: isAuthError(error),
    isValidation: isValidationError(error),
    isNetwork: isNetworkError(error),
    originalError: error,
  };
};

export const logError = (error, context = null) => {
  if (process.env.NODE_ENV === "development") {
    console.error(`[Error Handler] ${context || "Erro"}:`, {
      error,
      formatted: formatError(error, context),
      statusCode: extractStatusCode(error),
    });
  }
};

const errorHandler = {
  toUserErrorMessage,
  formatError,
  formatSuccess,
  handleError,
  logError,
  isAuthError,
  isValidationError,
  isNetworkError,
  ERROR_MESSAGES,
  OPERATION_MESSAGES,
};

export default errorHandler;
